import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  DataState,
  LeaderboardFilterChips,
  PageHeader,
} from "@vms/ui-web";
import { useOrgContext } from "../../context/OrgContext";
import {
  useDeleteLeaderboard,
  useOrgLeaderboards,
} from "../../api/leaderboards";
import { PushToDisplaysModal } from "../../components/leaderboards/PushToDisplaysModal";

/**
 * Org-level leaderboards list — cloud-authored cross-venue records that live
 * only on the cloud and aggregate lap-times across one or more of the org's
 * venues. Hub-authored single-venue leaderboards have their own page under
 * each venue (`VenueLeaderboards`); this page only lists the cloud-authored
 * ones (server filters by `venue_id IS NULL`).
 */
export function OrgLeaderboards() {
  const navigate = useNavigate();
  const { currentOrg, venues } = useOrgContext();
  const { data: leaderboards, loading, error, refetch } = useOrgLeaderboards(
    currentOrg?.id,
  );
  const del = useDeleteLeaderboard();
  const [pushTarget, setPushTarget] = useState<{ id: string; name: string } | null>(null);

  if (!currentOrg) return null;
  const rows = leaderboards ?? [];
  const venueNameById = new Map(venues.map((v) => [v.id, v.name]));

  const describeScope = (venueIds: string[] | null | undefined): string => {
    if (!venueIds) return "All venues";
    if (venueIds.length === 0) return "All venues";
    if (venueIds.length === 1) return venueNameById.get(venueIds[0]) ?? "1 venue";
    if (venueIds.length <= 3) {
      return venueIds
        .map((id) => venueNameById.get(id) ?? id.slice(0, 6))
        .join(", ");
    }
    return `${venueIds.length} venues`;
  };

  async function handleDelete(id: string, name: string): Promise<void> {
    if (!confirm(`Delete leaderboard "${name}"? It can be restored from the archive list later.`)) {
      return;
    }
    try {
      await del.mutate({ id });
      refetch();
    } catch (e) {
      alert(`Delete failed: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Leaderboards"
        subtitle="Cross-venue leaderboards. Aggregate lap times across one, several, or all venues in this organisation."
        actions={
          <Button onClick={() => navigate(`/org/${currentOrg.slug}/leaderboards/new`)}>
            New leaderboard
          </Button>
        }
      />
      <DataState
        loading={loading}
        error={error}
        empty={!loading && rows.length === 0}
        emptyMessage="No cross-venue leaderboards yet. Create one to get started."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {rows.map((lb) => (
            <Card key={lb.id} className="p-4 flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-gray-900 truncate">{lb.name}</div>
                <div className="text-xs text-gray-500 mt-1">
                  {describeScope(lb.venueIds)}
                  {lb.subheading ? ` · ${lb.subheading}` : ""}
                </div>
                <div className="mt-2">
                  <LeaderboardFilterChips
                    game={lb.game}
                    eventIds={lb.eventIds}
                    tracks={lb.tracks}
                    cars={lb.cars}
                    dateFrom={lb.dateFrom}
                    dateTo={lb.dateTo}
                  />
                </div>
                <div className="text-xs text-gray-400 mt-2">
                  Updated {new Date(lb.updatedAt).toLocaleString()}
                </div>
              </div>
              <div className="flex flex-col gap-2 flex-shrink-0">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    window.open(`/display/leaderboard/${lb.id}`, "_blank", "noopener")
                  }
                >
                  Open display
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setPushTarget({ id: lb.id, name: lb.name })}
                >
                  Push to displays
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => navigate(`/org/${currentOrg.slug}/leaderboards/${lb.id}/edit`)}
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => handleDelete(lb.id, lb.name)}
                  disabled={del.loading}
                >
                  Delete
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </DataState>
      {pushTarget && (
        <PushToDisplaysModal
          isOpen={true}
          onClose={() => setPushTarget(null)}
          organisationId={currentOrg.id}
          leaderboardId={pushTarget.id}
          leaderboardName={pushTarget.name}
        />
      )}
    </div>
  );
}
